import { Block } from "@block";
import { StackSpacing } from "@components/spacing";
import { useDesktopMediaQuery } from "@media-query";
import { getListAccountSelector } from "app/modules/account-management/reducer/selectors";
import { showModal as showModalRedux } from "app/modules/modal-management/reducer/action";
import { InputSelect, InputText } from "app/shared/components/form/input";
import AccountSelectModal from "app/shared/components/modal/bene-account-source/accounts-select.modal";
import AccountSourceModal from "app/shared/components/modal/bene-account-source/accounts-source.modal";
import GlobalModal, { createGlobalModal, globalModal } from "app/shared/components/modal/global-modal/global-modal";
import { Constant } from "app/shared/constant/constant";
import { ACCOUNT } from "app/shared/services/services-model.type";
import { mapAccountPayments } from "app/shared/util/common";
import { formatNumberThousand } from "app/shared/util/format-currency";
import React, { useEffect } from "react";
import { RegisterOptions, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Subheading, TextInput, useTheme } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { getTermDepositListStatementSelector } from "../../reducer/selectors";

export interface SourceTermDepositStatementProps {
  onChangeDebit: (accountDebit: ACCOUNT, isValid: boolean) => void;
  onChangeCredit: (accountCredit: ACCOUNT, isValid: boolean) => void;
  accountDebit?: ACCOUNT;
  accountCredit?: ACCOUNT;
  typeArray?: Array<string>;
  isSubmit?: boolean;
  isAccountCreditDefault?: boolean;
  isAccountDebitDefault?: boolean;
}

enum FormEnum {
  passbook,
  amountCash,
  accountNo,
  availableBalance,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface SourceAccountData {
  passbook: string;
  amountCash: string;
  accountNo: string;
  availableBalance: string;
}

const SourceTermDepositStatement = (props: SourceTermDepositStatementProps) => {
  const {
    accountDebit,
    accountCredit,
    typeArray,
    isSubmit,
    onChangeDebit = () => {},
    onChangeCredit = () => {},
    isAccountCreditDefault = false,
    isAccountDebitDefault = false,
  } = props;
  const { colors, fonts } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const { t } = useTranslation();
  const dispatchStore = useDispatch();
  const listAccountStore = useSelector(getListAccountSelector);
  const listPassBook = useSelector(getTermDepositListStatementSelector)?.LIST_ACCOUNT_PROFILE;

  const [globalModalRef] = React.useState<React.RefObject<any>>(createGlobalModal());
  const [accountSelectDebit, setAccountDebit] = React.useState<ACCOUNT | undefined>(accountDebit);
  const [accountSelectCredit, setAccountCredit] = React.useState<ACCOUNT | undefined>();
  const [listAccountCredit, setListAccountCredit] = React.useState<ACCOUNT[]>([]);
  const [listAccountDebit, setListAccountDebit] = React.useState<ACCOUNT[]>([]);

  const { control, setValue, getValues, clearErrors, trigger } = useForm<SourceAccountData>({
    defaultValues: {
      passbook: "",
      amountCash: "",
      accountNo: "",
      availableBalance: "",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    passbook: {
      required: {
        value: true,
        message: t("SAVING_VUI_LONG_CHON_SO_TIET_KIEM"),
      },
    },
    amountCash: {
      required: {
        value: false,
        message: "",
      },
    },
    accountNo: {
      required: {
        value: true,
        message: t("SAVING_VUI_LONG_CHON_TAI_KHOAN_NHAN"),
      },
    },
    availableBalance: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  const getAccountDefaultCredit = React.useCallback(
    (list: ACCOUNT[]) => {
      if (!accountCredit?.ACCOUNTNO && list && list.length) {
        let accountDefault = list[0];
        if (list.length > 1) {
          const accountS = list.filter((item) => {
            if (typeArray?.length && typeArray.length === 1 && typeArray[0] === Constant.ACCOUNTTYPE.ACCOUNT_PAYMENT) {
              return item.PRODUCTCODE === Constant.PRODUCT_CODE_PAYMENT;
            }
            return;
          });
          if (accountS && accountS.length) accountDefault = accountS[0];
        }
        return accountDefault;
      }
      return accountCredit;
    },
    [accountCredit, typeArray],
  );

  const getListAccountCredit = React.useCallback(
    (data?: ACCOUNT[]) => {
      let list: ACCOUNT[] = [];
      if (data?.length) {
        const filter = data.filter((item) => {
          if (typeArray?.length) {
            return item.STATUS === "A" && item.ACCOUNTTYPE?.length && typeArray.includes(item.ACCOUNTTYPE) && item.CURRENCYCODE === "VND";
          } else {
            return item.STATUS === "A" && item.CURRENCYCODE === "VND";
          }
        });
        list = filter;
      }
      return mapAccountPayments(list);
    },
    [typeArray],
  );

  const getListAccountDebit = React.useCallback((data?: ACCOUNT[]) => {
    const list: ACCOUNT[] = [];
    if (data?.length) {
      const filter = data.filter((item) => {
        return (
          item.ACCOUNTTYPE &&
          Constant.ACCOUNTTYPE.ACCOUNT_TERM_DEPOSIT.includes(item.ACCOUNTTYPE) &&
          Number.isInteger(Number(item.CURRENTBALANCE || "0")) &&
          Number(item.CURRENTBALANCE || "0") > 0 &&
          item.STATUS !== "C" &&
          item.CURRENCYCODE === "VND"
        );
      });
      return filter;
    }
    return list;
  }, []);

  const handlerSelectAccountDebit = (acc: ACCOUNT) => {
    globalModal.hide(globalModalRef);
    setAccountDebit(acc);
  };

  const handlerSelectAccountCredit = (acc: ACCOUNT) => {
    globalModal.hide(globalModalRef);
    setAccountCredit(acc);
  };

  const onPressChooseDebit = () => {
    if (listAccountDebit.length) {
      const content: JSX.Element = (
        <AccountSelectModal
          account={accountSelectDebit}
          typeArray={Constant.ACCOUNTTYPE.ACCOUNT_TERM_DEPOSIT}
          data={listAccountDebit}
          checkCurrentCode={true}
          onPressAcceptAccount={handlerSelectAccountDebit}
          title={t("SAVING_CHON_SO_TIET_KIEM")}
        />
      );
      globalModal.show(content, globalModalRef);
    } else {
      dispatchStore(
        showModalRedux({
          modalProps: {
            open: true,
            message: t("SAVING_KHONG_CO_SO_TIET_KIEM_TAT_TOAN"),
          },
          modalType: "alert",
        }),
      );
    }
  };

  const onPressChooseCredit = () => {
    const content: JSX.Element = (
      <AccountSourceModal
        account={accountSelectCredit}
        typeArray={[Constant.ACCOUNTTYPE.ACCOUNT_PAYMENT]}
        checkCurrentCode={true}
        onPressAcceptAccount={handlerSelectAccountCredit}
      />
    );
    globalModal.show(content, globalModalRef);
  };

  const handlerChangeDebit = React.useCallback(
    async (account: ACCOUNT) => {
      const accountTrigger = await trigger("passbook");
      if (account?.ACCOUNTNO?.length) onChangeDebit(account, Boolean(accountTrigger));
    },
    [onChangeDebit, trigger],
  );

  const handlerChangeCredit = React.useCallback(
    async (account: ACCOUNT) => {
      const accountTrigger = await trigger("accountNo");
      if (account?.ACCOUNTNO?.length) onChangeCredit(account, Boolean(accountTrigger));
    },
    [onChangeCredit, trigger],
  );

  useEffect(() => {
    if (listAccountStore?.ACCOUNT.length) {
      const list = getListAccountCredit(listAccountStore.ACCOUNT);
      setListAccountCredit(list);
    }
  }, [getListAccountCredit, listAccountStore]);

  useEffect(() => {
    if (listAccountCredit.length && !accountSelectCredit?.ACCOUNTNO?.length) {
      setAccountCredit(getAccountDefaultCredit(listAccountCredit));
    }
  }, [listAccountCredit, accountSelectCredit, getAccountDefaultCredit]);

  useEffect(() => {
    if (listPassBook?.ACCOUNT.length) {
      const list = getListAccountDebit(listPassBook.ACCOUNT);
      setListAccountDebit(list);
    }
  }, [getListAccountDebit, listPassBook]);

  useEffect(() => {
    if (listAccountDebit.length && !accountSelectDebit?.ACCOUNTNO?.length) {
      setAccountDebit(listAccountDebit[0]);
    }
  }, [listAccountDebit, accountSelectDebit]);

  useEffect(() => {
    if (accountSelectDebit?.ACCOUNTNO?.length && accountSelectDebit?.ACCOUNTNO !== getValues("passbook")) {
      clearErrors("passbook");
      clearErrors("amountCash");
      setValue("passbook", accountSelectDebit.ACCOUNTNO || "");
      setValue("amountCash", formatNumberThousand(accountSelectDebit.CURRENTBALANCE));
      handlerChangeDebit(accountSelectDebit);
    }
  }, [accountSelectDebit, clearErrors, handlerChangeDebit, setValue, getValues]);

  useEffect(() => {
    if (accountSelectCredit?.ACCOUNTNO?.length && accountSelectCredit?.ACCOUNTNO !== getValues("accountNo")) {
      clearErrors("accountNo");
      clearErrors("availableBalance");
      setValue("accountNo", accountSelectCredit.ACCOUNTNO || "");
      setValue("availableBalance", formatNumberThousand(accountSelectCredit.AVAILABLEBALANCE));
      handlerChangeCredit(accountSelectCredit);
    }
  }, [accountSelectCredit, clearErrors, getValues, handlerChangeCredit, setValue]);

  useEffect(() => {
    if (isAccountDebitDefault && accountDebit?.ACCOUNTNO?.length && accountSelectDebit?.ACCOUNTNO) {
      setAccountDebit(accountDebit);
    }
  }, [accountSelectDebit, accountDebit, isAccountDebitDefault]);

  useEffect(() => {
    if (isAccountCreditDefault && accountCredit?.ACCOUNTNO?.length && accountSelectCredit?.ACCOUNTNO) {
      setAccountCredit(accountCredit);
    }
  }, [accountSelectCredit, accountCredit, isAccountCreditDefault]);

  useEffect(() => {
    if (isSubmit) {
      trigger("passbook");
      trigger("accountNo");
    }
  }, [isSubmit, trigger]);

  const chooseAccountDebit = <TextInput.Icon name="chevron-down" onPress={onPressChooseDebit} />;
  const chooseAccountCredit = <TextInput.Icon name="chevron-down" onPress={onPressChooseCredit} />;
  const rightAmount = <TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />;

  return (
    <Block pt={16} row={isDesktop ? true : false}>
      <Block w={200}>
        <StackSpacing size="medium" />
        <Subheading style={{ ...fonts.semibold }} numberOfLines={1}>
          {t("SAVING_THONG_TIN_GIAO_DICH")}
        </Subheading>
        <StackSpacing size="medium" />
      </Block>
      <Block flex={1} bg={colors.backgroundForm} br={12} p={16}>
        <InputSelect
          control={control}
          name="passbook"
          label={t("SAVING_SO_TIET_KIEM")}
          rules={validateSchemeForm.passbook}
          onClick={onPressChooseDebit}
          inputProps={{ right: chooseAccountDebit, editable: false }}
        />
        <InputText rules={validateSchemeForm.amountCash} name="amountCash" control={control} label={t("SAVING_SO_TIEN_RUT")} />
        <InputSelect
          control={control}
          name="accountNo"
          label={t("SAVING_CHUYEN_VAO_TAI_KHOAN")}
          rules={validateSchemeForm.accountNo}
          onClick={onPressChooseCredit}
          inputProps={{ right: chooseAccountCredit, editable: false }}
        />
        <InputText
          rules={validateSchemeForm.availableBalance}
          name="availableBalance"
          control={control}
          label={t("ACC_SO_DU_KHA_DUNG")}
          inputProps={{ right: rightAmount, editable: false }}
        />
      </Block>
      <GlobalModal ref={globalModalRef} />
    </Block>
  );
};

export default SourceTermDepositStatement;
